// ── Multi-Provider Streaming Architecture ──
// NUNCA atar lógica a un proveedor específico.
// Toda lógica depende del TIPO de proveedor, no del ID del proveedor.
//
// Agregar un nuevo proveedor = registrarlo en la registry con su tipo.
// Cero cambios en lógica de negocio.

// ═══════════════════════════════════════════════
// PROVIDER TYPE — Categoría abstracta.
// El código SIEMPRE chequea esto, nunca el ID.
// ═══════════════════════════════════════════════

/** Categoría abstracta de proveedor. La lógica depende de esto. */
export type StreamProviderType =
  | 'direct_server'   // Servidor de streaming directo (Shoutcast, Icecast)
  | 'managed_panel'   // Panel administrado con AutoDJ (AzuraCast, CentovaCast)
  | 'rfw_hosted'       // Hosting administrado por RFW
  | 'custom_url';      // URL personalizada / otro proveedor

/** Capacidades que un tipo de proveedor puede ofrecer */
export type ProviderCapability =
  | 'autodj'           // AutoDJ / programación automática
  | 'statistics'       // Estadísticas de oyentes
  | 'remote_control'   // Control remoto del servidor
  | 'fallback'         // Servidor de respaldo
  | 'hls'              // Soporte HLS
  | 'metadata'         // Metadata de pista actual
  | 'ssl'              // SSL/TLS integrado
  | 'multi_bitrate';   // Múltiples bitrates

// ═══════════════════════════════════════════════
// PROVIDER ID — Implementación específica.
// Solo se usa para configuración y labels.
// ═══════════════════════════════════════════════

/** Proveedor específico. Solo para configuración, NUNCA para lógica. */
export type StreamProviderId =
  | 'shoutcast'
  | 'icecast'
  | 'azuracast'
  | 'centovacast'
  | 'custom'
  | 'none';

/** Metadata de un proveedor específico — definida en la registry */
export interface StreamProviderMeta {
  id: StreamProviderId;
  type: StreamProviderType;
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
  capabilities: ProviderCapability[];
  defaultPort: number;
  defaultFormat: string;
  protocolHints: string[];
}

/** Registry completa: mapea cada ID a su metadata */
export type StreamProviderRegistry = Record<StreamProviderId, StreamProviderMeta>;

// ═══════════════════════════════════════════════
// HOSTING TYPE — Cómo RFW recibe el stream
// ═══════════════════════════════════════════════

export type HostingType = 'external' | 'rfw_hosting';

// ═══════════════════════════════════════════════
// STREAM STATUS
// ═══════════════════════════════════════════════

export type StreamStatus = 'online' | 'offline' | 'pending' | 'fallback';

// ═══════════════════════════════════════════════
// TECHNICAL STREAM STATUS — Estados reales de CentovaCast
// Estos reflejan el estado del servidor de streaming,
// NO el estado comercial de la radio ni el estado de reproducción.
// ═══════════════════════════════════════════════

/** Estado técnico del servidor de streaming (CentovaCast + ciclo de vida RFW) */
export type TechnicalStreamStatus =
  | 'not_configured'        // No se ha configurado ningún proveedor
  | 'pending_provisioning'  // Esperando aprovisionamiento del servidor
  | 'provisioning'          // Servidor en creación/configuración
  | 'active'                // Servidor operativo, stream disponible
  | 'suspended'             // Servidor suspendido (impago, violación de ToS)
  | 'offline'               // Servidor configurado pero sin stream (AutoDJ detenido, source desconectado)
  | 'error'                 // Error técnico en el servidor
  | 'deletion_pending'      // Servidor marcado para eliminación
  | 'deleted';              // Servidor eliminado

/** Información pública segura del streaming. NUNCA exponer credenciales, puertos, server_id, etc. */
export interface PublicStreamInfo {
  technicalStreamStatus: TechnicalStreamStatus;
  streamAvailable: boolean;
  listenerCount: number;
  currentMetadata: string | null;
  lastTechnicalSyncAt: string | null;
  publicStreamUrl: string | null;
  publicSecureStreamUrl: string | null;
}

// ── Labels & Display ──

export const technicalStreamStatusLabels: Record<TechnicalStreamStatus, string> = {
  not_configured: 'Sin configurar',
  pending_provisioning: 'Aprovisionamiento pendiente',
  provisioning: 'Configurando servidor',
  active: 'Streaming activo',
  suspended: 'Suspendido',
  offline: 'Fuera del aire',
  error: 'Error técnico',
  deletion_pending: 'Eliminación pendiente',
  deleted: 'Eliminado',
};

/** Etiquetas cortas para badges y estados compactos */
export const technicalStreamStatusShortLabels: Record<TechnicalStreamStatus, string> = {
  not_configured: 'Setup pendiente',
  pending_provisioning: 'Pendiente',
  provisioning: 'Configurando',
  active: 'En vivo',
  suspended: 'Suspendido',
  offline: 'Offline',
  error: 'Error',
  deletion_pending: 'Eliminando',
  deleted: 'Eliminado',
};

/** Colores para badges de estado técnico */
export const technicalStreamStatusColors: Record<TechnicalStreamStatus, { dot: string; bg: string; text: string }> = {
  active:             { dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  offline:            { dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' },
  not_configured:     { dot: 'bg-foreground-300', bg: 'bg-foreground-100', text: 'text-foreground-600' },
  pending_provisioning: { dot: 'bg-sky-400', bg: 'bg-sky-100', text: 'text-sky-700' },
  provisioning:       { dot: 'bg-sky-500 animate-pulse', bg: 'bg-sky-100', text: 'text-sky-700' },
  suspended:          { dot: 'bg-red-500', bg: 'bg-red-100', text: 'text-red-700' },
  error:              { dot: 'bg-red-600', bg: 'bg-red-100', text: 'text-red-700' },
  deletion_pending:   { dot: 'bg-foreground-400', bg: 'bg-foreground-100', text: 'text-foreground-600' },
  deleted:            { dot: 'bg-foreground-300', bg: 'bg-foreground-100', text: 'text-foreground-500' },
};

/** Iconos para cada estado técnico */
export const technicalStreamStatusIcons: Record<TechnicalStreamStatus, string> = {
  active:             'ri-broadcast-line',
  offline:            'ri-signal-wifi-off-line',
  not_configured:     'ri-settings-3-line',
  pending_provisioning: 'ri-hourglass-line',
  provisioning:       'ri-loader-4-line',
  suspended:          'ri-forbid-2-line',
  error:              'ri-error-warning-line',
  deletion_pending:   'ri-delete-bin-line',
  deleted:            'ri-delete-bin-6-line',
};

/** Mensajes públicos para cada estado — mostrados en el player y en el perfil */
export const technicalStreamStatusMessages: Record<TechnicalStreamStatus, string> = {
  active:             'Transmitiendo en vivo. ¡Dale play!',
  offline:            'La estación no está transmitiendo en este momento. Podés escuchar RFW Radio mientras tanto.',
  not_configured:     'Esta radio está configurando su streaming. Pronto estará disponible.',
  pending_provisioning: 'El servidor de streaming está siendo aprovisionado. Volvé en unos minutos.',
  provisioning:       'Estamos preparando el servidor de streaming. Esto puede tomar unos minutos.',
  suspended:          'Esta radio está temporalmente suspendida. No es posible reproducir su contenido.',
  error:              'Hay un problema técnico con el servidor de streaming. Estamos trabajando para solucionarlo.',
  deletion_pending:   'Esta radio será eliminada pronto. Su contenido ya no está disponible.',
  deleted:            'Esta radio ya no está disponible.',
};

/**
 * Determina si una radio puede ser reproducida según su estado técnico.
 * Solo active puede reproducir su propio stream. offline permite fallback a RFW.
 */
export function canPlayStream(status: TechnicalStreamStatus): boolean {
  return status === 'active';
}

/**
 * Determina si se debe aplicar fallback a RFW Radio.
 * Solo cuando el servidor está configurado pero offline (no suspended, no error, no deleted, etc.)
 */
export function shouldFallbackToRfw(status: TechnicalStreamStatus): boolean {
  return status === 'offline' || status === 'not_configured';
}

/**
 * Determina si el estado técnico es transitorio (el usuario debe esperar).
 */
export function isTransientStatus(status: TechnicalStreamStatus): boolean {
  return status === 'pending_provisioning' || status === 'provisioning';
}

/**
 * Determina si el estado técnico es terminal/de error (no hay recuperación automática).
 */
export function isTerminalStatus(status: TechnicalStreamStatus): boolean {
  return status === 'suspended' || status === 'error' || status === 'deletion_pending' || status === 'deleted';
}

// ═══════════════════════════════════════════════
// RADIO STREAM INFO — Info completa de streaming
// ═══════════════════════════════════════════════

export interface RadioStreamInfo {
  hostingType: HostingType;
  streamProvider: StreamProviderId;
  providerType: StreamProviderType;
  streamUrl: string;
  streamStatus: StreamStatus;
  fallbackUrl?: string;
  port?: number;
  mount?: string;
  format?: string;
  bitrate?: number;
}

// ═══════════════════════════════════════════════
// LABELS & DISPLAY
// ═══════════════════════════════════════════════

export const providerTypeLabels: Record<StreamProviderType, string> = {
  direct_server: 'Servidor directo',
  managed_panel: 'Panel administrado',
  rfw_hosted: 'Hosting RFW',
  custom_url: 'URL personalizada',
};

export const providerTypeIcons: Record<StreamProviderType, string> = {
  direct_server: 'ri-server-line',
  managed_panel: 'ri-dashboard-3-line',
  rfw_hosted: 'ri-cloud-line',
  custom_url: 'ri-global-line',
};

export const hostingTypeLabels: Record<HostingType, string> = {
  external: 'Streaming externo conectado',
  rfw_hosting: 'Streaming administrado por RFW',
};

export const streamStatusLabels: Record<StreamStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  pending: 'Pendiente',
  fallback: 'RFW Radio (respaldo)',
};

export const streamStatusColors: Record<StreamStatus, { dot: string; bg: string; text: string }> = {
  online: { dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  offline: { dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' },
  pending: { dot: 'bg-sky-500', bg: 'bg-sky-100', text: 'text-sky-700' },
  fallback: { dot: 'bg-foreground-400', bg: 'bg-foreground-100', text: 'text-foreground-600' },
};

// ═══════════════════════════════════════════════
// PROVIDER REGISTRY — Fuente única de verdad
// ═══════════════════════════════════════════════

/** Registry por defecto. Se puede extender con más proveedores. */
export const defaultProviderRegistry: StreamProviderRegistry = {
  shoutcast: {
    id: 'shoutcast',
    type: 'direct_server',
    label: 'SHOUTcast',
    shortLabel: 'SHOUTcast',
    description: 'Servidor de streaming clásico. MP3 y AAC+. Ampliamente compatible.',
    icon: 'ri-broadcast-line',
    capabilities: ['fallback', 'metadata', 'multi_bitrate'],
    defaultPort: 9000,
    defaultFormat: 'MP3',
    protocolHints: ['http', 'https'],
  },
  icecast: {
    id: 'icecast',
    type: 'direct_server',
    label: 'Icecast',
    shortLabel: 'Icecast',
    description: 'Servidor de streaming estándar. Soporta AAC, MP3, OGG. El más usado.',
    icon: 'ri-server-line',
    capabilities: ['fallback', 'metadata', 'ssl', 'multi_bitrate'],
    defaultPort: 8000,
    defaultFormat: 'AAC',
    protocolHints: ['http', 'https'],
  },
  azuracast: {
    id: 'azuracast',
    type: 'managed_panel',
    label: 'AzuraCast',
    shortLabel: 'AzuraCast',
    description: 'Panel de gestión completo con AutoDJ, estadísticas, webhooks y API REST.',
    icon: 'ri-dashboard-3-line',
    capabilities: ['autodj', 'statistics', 'remote_control', 'hls', 'metadata', 'ssl', 'multi_bitrate'],
    defaultPort: 443,
    defaultFormat: 'AAC+',
    protocolHints: ['https'],
  },
  centovacast: {
    id: 'centovacast',
    type: 'rfw_hosted',
    label: 'Centova Cast',
    shortLabel: 'Centova',
    description: 'Motor de streaming interno de RFW. Hosting administrado con AutoDJ, estadísticas y control total del servidor. Exclusivo para radios con Hosting RFW.',
    icon: 'ri-cloud-line',
    capabilities: ['autodj', 'statistics', 'remote_control', 'metadata', 'ssl', 'multi_bitrate'],
    defaultPort: 2199,
    defaultFormat: 'AAC+',
    protocolHints: ['http', 'https'],
  },
  custom: {
    id: 'custom',
    type: 'custom_url',
    label: 'Proveedor propio',
    shortLabel: 'Personalizado',
    description: 'Conectá cualquier servidor de streaming propio. URL personalizada.',
    icon: 'ri-global-line',
    capabilities: [],
    defaultPort: 0,
    defaultFormat: 'MP3',
    protocolHints: ['http', 'https', 'rtmp', 'rtmps'],
  },
  none: {
    id: 'none',
    type: 'custom_url',
    label: 'Sin configurar',
    shortLabel: 'Ninguno',
    description: 'No se ha configurado ningún proveedor de streaming.',
    icon: 'ri-settings-3-line',
    capabilities: [],
    defaultPort: 0,
    defaultFormat: '',
    protocolHints: [],
  },
};

// ═══════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════

/**
 * Obtiene el tipo de proveedor a partir de su ID.
 * Esta es LA función que toda lógica debe usar.
 * Ejemplo: if (getProviderType(radio.streamProvider) === 'direct_server') { ... }
 */
export function getProviderType(
  providerId: StreamProviderId,
  registry: StreamProviderRegistry = defaultProviderRegistry,
): StreamProviderType {
  return registry[providerId]?.type ?? 'custom_url';
}

/**
 * Obtiene las capacidades de un proveedor específico.
 */
export function getProviderCapabilities(
  providerId: StreamProviderId,
  registry: StreamProviderRegistry = defaultProviderRegistry,
): ProviderCapability[] {
  return registry[providerId]?.capabilities ?? [];
}

/**
 * Verifica si un proveedor tiene una capacidad específica.
 */
export function providerHasCapability(
  providerId: StreamProviderId,
  capability: ProviderCapability,
  registry: StreamProviderRegistry = defaultProviderRegistry,
): boolean {
  return getProviderCapabilities(providerId, registry).includes(capability);
}

/**
 * Obtiene todos los IDs de proveedores de un tipo específico.
 * Útil para mostrar opciones filtradas por tipo.
 */
export function getProvidersByType(
  type: StreamProviderType,
  registry: StreamProviderRegistry = defaultProviderRegistry,
): StreamProviderId[] {
  return (Object.entries(registry) as [StreamProviderId, StreamProviderMeta][])
    .filter(([, meta]) => meta.type === type)
    .map(([id]) => id);
}

/**
 * Obtiene el label de un proveedor específico.
 */
export function getProviderLabel(
  providerId: StreamProviderId,
  registry: StreamProviderRegistry = defaultProviderRegistry,
): string {
  return registry[providerId]?.label ?? providerId;
}

/**
 * Deriva el ProviderType a partir del HostingType y StreamProviderId.
 * Si es rfw_hosting, siempre es 'rfw_hosted'.
 * Si es external, se deriva del provider específico.
 */
export function deriveProviderType(
  hostingType: HostingType,
  providerId: StreamProviderId,
  registry: StreamProviderRegistry = defaultProviderRegistry,
): StreamProviderType {
  if (hostingType === 'rfw_hosting') return 'rfw_hosted';
  return getProviderType(providerId, registry);
}

/**
 * Determina si un proveedor es de tipo managed_panel (AzuraCast, CentovaCast, etc.)
 */
export function isManagedPanel(providerId: StreamProviderId): boolean {
  return getProviderType(providerId) === 'managed_panel';
}

/**
 * Determina si un proveedor es de tipo direct_server (Shoutcast, Icecast)
 */
export function isDirectServer(providerId: StreamProviderId): boolean {
  return getProviderType(providerId) === 'direct_server';
}

/**
 * Determina si un proveedor tiene AutoDJ
 */
export function hasAutoDJ(providerId: StreamProviderId): boolean {
  return providerHasCapability(providerId, 'autodj');
}